import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, AlertCircle, Settings, GitBranch, Loader2, Filter, X, Calendar } from 'lucide-react';
import { incidentAPI, serviceRequestAPI, changeRequestAPI, applicationAPI, categoryAPI } from '../services/api';

const stClass = {
  new: 'bg-slate-100 text-slate-700', assigned: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-emerald-100 text-emerald-800', pending_user: 'bg-amber-100 text-amber-800',
  pending_approval: 'bg-purple-100 text-purple-800', resolved: 'bg-emerald-200 text-emerald-900',
  closed: 'bg-slate-200 text-slate-600', cancelled: 'bg-rose-100 text-rose-700',
};
const prClass = { p1: 'bg-rose-100 text-rose-800', p2: 'bg-amber-100 text-amber-800', p3: 'bg-blue-100 text-blue-800', p4: 'bg-slate-200 text-slate-700' };
const typeIcon = { incident: AlertCircle, sr: Settings, cr: GitBranch };
const typeColor = { incident: 'text-rose-600', sr: 'text-blue-600', cr: 'text-purple-600' };
const typeLabel = { incident: 'Incident', sr: 'Service Request', cr: 'Change Request' };

export default function MyTicketsSearchPage() {
  const navigate = useNavigate();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [apps, setApps] = useState([]);
  const [cats, setCats] = useState([]);

  const [filters, setFilters] = useState({
    ticket_number: '',
    ticket_type: 'all',
    application: '',
    category: '',
    status: '',
    date_from: '',
    date_to: '',
    search: '',
  });

  useEffect(() => {
    Promise.all([applicationAPI.list(), categoryAPI.listFlat()]).then(([aRes, cRes]) => {
      setApps(aRes.data?.results || aRes.data || []);
      setCats(cRes.data?.results || cRes.data || []);
    }).catch(() => {});
  }, []);

  const handleSearch = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setSearched(true);
    try {
      const params = { search_scope: 'global' };  // bypass reporter filter for end users
      if (filters.search) params.search = filters.search;
      if (filters.application) params.application = filters.application;
      if (filters.category) params.category = filters.category;
      if (filters.status) params.status = filters.status;
      if (filters.date_from) params.created_after = filters.date_from;
      if (filters.date_to) params.created_before = filters.date_to;

      const ticketNumberUpper = filters.ticket_number.trim().toUpperCase();

      const apis = [];
      if (filters.ticket_type === 'all' || filters.ticket_type === 'incident') apis.push({ api: incidentAPI, type: 'incident' });
      if (filters.ticket_type === 'all' || filters.ticket_type === 'sr') apis.push({ api: serviceRequestAPI, type: 'sr' });
      if (filters.ticket_type === 'all' || filters.ticket_type === 'cr') apis.push({ api: changeRequestAPI, type: 'cr' });

      const responses = await Promise.all(apis.map(({ api }) => api.list(params).catch(() => ({ data: [] }))));

      let all = [];
      responses.forEach(({ data }, idx) => {
        const type = apis[idx].type;
        const items = data?.results || data || [];
        items.forEach(t => all.push({ ...t, _type: type }));
      });

      // Filter by ticket number client-side if provided
      if (ticketNumberUpper) {
        all = all.filter(t => t.ticket_number?.toUpperCase().includes(ticketNumberUpper));
      }

      // Sort by created_at desc
      all.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setResults(all);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setFilters({ ticket_number: '', ticket_type: 'all', application: '', category: '', status: '', date_from: '', date_to: '', search: '' });
    setResults([]);
    setSearched(false);
  };

  const f = (key, val) => setFilters(p => ({ ...p, [key]: val }));

  return (
    <div data-testid="my-tickets-search-page">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
          <Search className="w-6 h-6 text-sky-500" /> Search Tickets
        </h2>
        <p className="text-sm text-slate-500 mt-1">Search all Incidents, Service Requests, and Change Requests across SupportOne</p>
      </div>

      {/* Filter Panel */}
      <form onSubmit={handleSearch} className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 shadow-sm p-5 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
          {/* Ticket number */}
          <div>
            <label className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1 block">Ticket Number</label>
            <input
              type="text" placeholder="e.g. INC-0031, SR-0005"
              value={filters.ticket_number} onChange={e => f('ticket_number', e.target.value)}
              className="w-full border dark:border-slate-700 rounded-xl px-3 py-2.5 text-sm dark:bg-slate-900 dark:text-white"
              data-testid="filter-ticket-number"
            />
          </div>

          {/* Keyword search */}
          <div>
            <label className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1 block">Keyword Search</label>
            <input
              type="text" placeholder="Search in title / description"
              value={filters.search} onChange={e => f('search', e.target.value)}
              className="w-full border dark:border-slate-700 rounded-xl px-3 py-2.5 text-sm dark:bg-slate-900 dark:text-white"
              data-testid="filter-keyword"
            />
          </div>

          {/* Ticket type */}
          <div>
            <label className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1 block">Ticket Type</label>
            <select value={filters.ticket_type} onChange={e => f('ticket_type', e.target.value)} className="w-full border dark:border-slate-700 rounded-xl px-3 py-2.5 text-sm dark:bg-slate-900 dark:text-white" data-testid="filter-type">
              <option value="all">All Types</option>
              <option value="incident">Incident</option>
              <option value="sr">Service Request</option>
              <option value="cr">Change Request</option>
            </select>
          </div>

          {/* Application */}
          <div>
            <label className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1 block">Application / System</label>
            <select value={filters.application} onChange={e => f('application', e.target.value)} className="w-full border dark:border-slate-700 rounded-xl px-3 py-2.5 text-sm dark:bg-slate-900 dark:text-white" data-testid="filter-application">
              <option value="">All Applications</option>
              {apps.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>

          {/* Category */}
          <div>
            <label className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1 block">Category</label>
            <select value={filters.category} onChange={e => f('category', e.target.value)} className="w-full border dark:border-slate-700 rounded-xl px-3 py-2.5 text-sm dark:bg-slate-900 dark:text-white" data-testid="filter-category">
              <option value="">All Categories</option>
              {cats.filter(c => !c.parent).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          {/* Status */}
          <div>
            <label className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1 block">Status</label>
            <select value={filters.status} onChange={e => f('status', e.target.value)} className="w-full border dark:border-slate-700 rounded-xl px-3 py-2.5 text-sm dark:bg-slate-900 dark:text-white" data-testid="filter-status">
              <option value="">All Statuses</option>
              <option value="new">New</option>
              <option value="assigned">Assigned</option>
              <option value="in_progress">In Progress</option>
              <option value="pending_approval">Pending Approval</option>
              <option value="pending_user">Pending User</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
          </div>

          {/* Date From */}
          <div>
            <label className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1 block flex items-center gap-1"><Calendar className="w-3 h-3" /> Created From</label>
            <input type="date" value={filters.date_from} onChange={e => f('date_from', e.target.value)} className="w-full border dark:border-slate-700 rounded-xl px-3 py-2.5 text-sm dark:bg-slate-900 dark:text-white" data-testid="filter-date-from" />
          </div>

          {/* Date To */}
          <div>
            <label className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1 block flex items-center gap-1"><Calendar className="w-3 h-3" /> Created To</label>
            <input type="date" value={filters.date_to} onChange={e => f('date_to', e.target.value)} className="w-full border dark:border-slate-700 rounded-xl px-3 py-2.5 text-sm dark:bg-slate-900 dark:text-white" data-testid="filter-date-to" />
          </div>
        </div>

        <div className="flex gap-3">
          <button type="submit" className="bg-sky-500 text-white px-6 py-2.5 rounded-xl text-sm flex items-center gap-2 hover:bg-sky-600" data-testid="search-btn">
            <Search className="w-4 h-4" /> Search
          </button>
          <button type="button" onClick={clearFilters} className="bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 px-4 py-2.5 rounded-xl text-sm flex items-center gap-2 hover:bg-slate-200">
            <X className="w-4 h-4" /> Clear
          </button>
        </div>
      </form>

      {/* Results */}
      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-sky-500" /></div>
      ) : searched ? (
        results.length === 0 ? (
          <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 p-12 text-center">
            <Filter className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500 font-medium">No tickets found</p>
            <p className="text-slate-400 text-sm mt-1">Try adjusting your search filters</p>
          </div>
        ) : (
          <>
          <div className="hidden md:block bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 overflow-hidden shadow-sm">
            <div className="px-4 py-3 bg-slate-50 dark:bg-slate-900 text-xs text-slate-500 border-b dark:border-slate-700">
              {results.length} ticket{results.length !== 1 ? 's' : ''} found
            </div>
            <table className="w-full" data-testid="search-results-table">
              <thead className="bg-slate-50 dark:bg-slate-900 text-xs text-slate-600 dark:text-slate-400">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Ticket</th>
                  <th className="px-4 py-3 text-left font-semibold">Title</th>
                  <th className="px-4 py-3 text-center font-semibold">Type</th>
                  <th className="px-4 py-3 text-center font-semibold">Status</th>
                  <th className="px-4 py-3 text-left font-semibold">Application</th>
                  <th className="px-4 py-3 text-left font-semibold">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y dark:divide-slate-700 text-sm">
                {results.map(t => {
                  const Icon = typeIcon[t._type] || AlertCircle;
                  const path = `/ticket/${t._type}/${t.id}`;
                  return (
                    <tr key={`${t._type}-${t.id}`} onClick={() => navigate(path)}
                      className="hover:bg-slate-50 dark:hover:bg-slate-700/50 cursor-pointer" data-testid={`result-${t.ticket_number}`}>
                      <td className="px-4 py-3">
                        <span className={`font-mono font-bold ${typeColor[t._type]}`}>{t.ticket_number}</span>
                      </td>
                      <td className="px-4 py-3 dark:text-white max-w-xs truncate">{t.title}</td>
                      <td className="px-4 py-3 text-center">
                        <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                          <Icon className={`w-3 h-3 ${typeColor[t._type]}`} />
                          {typeLabel[t._type]}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`${stClass[t.status] || 'bg-slate-100 text-slate-700'} px-2 py-1 rounded-full text-xs`}>
                          {t.status?.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-600 dark:text-slate-400 text-xs">{t.application_name || '—'}</td>
                      <td className="px-4 py-3 text-slate-500 text-xs">{t.created_at ? new Date(t.created_at).toLocaleDateString() : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            <p className="text-xs text-slate-400">{results.length} ticket{results.length !== 1 ? 's' : ''} found</p>
            {results.map(t => {
              const Icon = typeIcon[t._type] || AlertCircle;
              const path = `/ticket/${t._type}/${t.id}`;
              return (
                <div key={`${t._type}-${t.id}`} onClick={() => navigate(path)}
                  className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 p-4 shadow-sm cursor-pointer"
                  data-testid={`result-${t.ticket_number}`}>
                  <div className="flex items-start justify-between mb-1">
                    <span className={`font-mono font-bold text-sm ${typeColor[t._type]}`}>{t.ticket_number}</span>
                    <span className={`${stClass[t.status] || 'bg-slate-100 text-slate-700'} px-2 py-0.5 rounded-full text-xs flex-shrink-0 ml-2`}>{t.status?.replace(/_/g, ' ')}</span>
                  </div>
                  <div className="text-sm font-medium dark:text-white mb-1">{t.title}</div>
                  <div className="flex items-center gap-2 text-xs text-slate-400">
                    <span className="inline-flex items-center gap-1"><Icon className={`w-3 h-3 ${typeColor[t._type]}`} />{typeLabel[t._type]}</span>
                    {t.application_name && <span>· {t.application_name}</span>}
                    <span>· {t.created_at ? new Date(t.created_at).toLocaleDateString() : '—'}</span>
                  </div>
                </div>
              );
            })}
          </div>
          </>
        )
      ) : (
        <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 p-12 text-center">
          <Search className="w-12 h-12 mx-auto text-slate-200 dark:text-slate-600 mb-3" />
          <p className="text-slate-400">Use the filters above to search your tickets</p>
        </div>
      )}
    </div>
  );
}
